import {
  CalendarTimeRangeConfig,
  FixedTimeRangeConfig,
  IntervalJSON,
  RelativeTimeRangeConfig,
} from '@highimpact-frontend/api-types';
import { buildCalendarTimeRanges } from './CalendarTimeRange';
import { buildFixedRangeName } from './FixedTimeRange';
import {
  pluralUnitFrenchLabel,
  singularUnitFrenchLabel,
} from './RelativeTimeRange';
import { changeHourToEndOfDay, changeHourToStartOfDay } from './date';

type CalendarUnit =
  | 'millisecond'
  | 'second'
  | 'minute'
  | 'hour'
  | 'day'
  | 'week'
  | 'month'
  | 'year';

function isRelativeOrCalendarNowInterval(
  interval: IntervalJSON
): interval is RelativeTimeRangeConfig | CalendarTimeRangeConfig {
  return (
    (interval.type === 'relative_time_range' ||
      interval.type === 'calendar_time_range') &&
    interval.relative_to === 'now'
  );
}

function isCompactRelativeOrCalendarInterval(
  interval: RelativeTimeRangeConfig | CalendarTimeRangeConfig
): boolean {
  return interval.quantity > 1 && interval.period_shift === 0;
}

function isDividedRelativeOrCalendarGroup(
  intervals: (RelativeTimeRangeConfig | CalendarTimeRangeConfig)[]
): boolean {
  if (intervals.length > 1) {
    return true;
  }
  if (intervals.length === 1) {
    return isCompactRelativeOrCalendarInterval(intervals[0]) ? false : true;
  }
  return true;
}

/**
 * Infer whether the division switch should start ON from saved intervals.
 */
export function inferDivideIntoSubintervals(
  defaultIntervals?: IntervalJSON[]
): boolean {
  if (!defaultIntervals || defaultIntervals.length === 0) {
    return true;
  }

  const relativeOrCalendarNow = defaultIntervals.filter(
    isRelativeOrCalendarNowInterval
  );
  if (relativeOrCalendarNow.length > 0) {
    return isDividedRelativeOrCalendarGroup(relativeOrCalendarNow);
  }

  if (
    defaultIntervals.every(
      (i): i is CalendarTimeRangeConfig => i.type === 'calendar_time_range'
    )
  ) {
    return defaultIntervals.length > 1;
  }

  if (
    defaultIntervals.every(
      (i): i is FixedTimeRangeConfig => i.type === 'fixed_time_range'
    )
  ) {
    return defaultIntervals.length > 1;
  }

  return true;
}

function formatRelativeDirectionPreview(
  quantity: number,
  unit: RelativeTimeRangeConfig['unit'],
  divided: boolean,
  directionLabel?: string
): string {
  const suffix = directionLabel ? ` (${directionLabel})` : '';
  const singular = singularUnitFrenchLabel(unit);
  const plural = pluralUnitFrenchLabel(unit);

  if (divided) {
    return `${quantity} intervalles de 1 ${singular} chacun${suffix}`;
  }

  const unitLabel = quantity > 1 ? plural : singular;
  return `1 intervalle de ${quantity} ${unitLabel}${suffix}`;
}

export function formatRelativeDivisionPreview(options: {
  quantity: number;
  unit: RelativeTimeRangeConfig['unit'];
  divided: boolean;
  includeFuture?: boolean;
  quantityFuture?: number;
  unitFuture?: RelativeTimeRangeConfig['unit'];
}): string {
  const {
    quantity,
    unit,
    divided,
    includeFuture = false,
    quantityFuture = 1,
    unitFuture = unit,
  } = options;

  const pastPreview = formatRelativeDirectionPreview(quantity, unit, divided);

  if (!includeFuture) {
    return pastPreview;
  }

  const futurePreview = formatRelativeDirectionPreview(
    quantityFuture,
    unitFuture,
    divided,
    'futur'
  );
  const pastWithLabel = formatRelativeDirectionPreview(
    quantity,
    unit,
    divided,
    'passé'
  );

  return `${pastWithLabel} et ${futurePreview}`;
}

function calendarUnitFrenchLabel(
  unit: CalendarUnit,
  count: number
): string {
  switch (unit) {
    case 'year':
      return count > 1 ? 'ans' : 'an';
    case 'month':
      return 'mois';
    default:
      return count > 1 ? `${unit}s` : unit;
  }
}

export function formatCalendarDivisionPreview(options: {
  start: Date;
  end: Date;
  unit: CalendarUnit;
  divided: boolean;
}): string | null {
  const { start, end, unit, divided } = options;
  const normalizedStart = changeHourToStartOfDay(start);
  const normalizedEnd = changeHourToEndOfDay(end);

  if (!divided) {
    const rangeName = buildFixedRangeName(normalizedStart, normalizedEnd);
    return `1 intervalle du ${rangeName}`;
  }

  const formatter = new Intl.DateTimeFormat(
    undefined,
    unit === 'month' ? { month: 'short' } : { year: 'numeric' }
  );
  const intervals = buildCalendarTimeRanges(
    normalizedStart,
    normalizedEnd,
    unit,
    formatter,
    false
  );
  const count = intervals.length;
  const unitLabel = calendarUnitFrenchLabel(unit, 1);
  return count > 1 ? `${count} intervalles de 1 ${unitLabel} chacun` : null;
}
